import { isObject } from "@/helpers";
import type { SortedField } from "@/types";
import { z } from "zod";
import { assertSchema } from "@/helpers/assert-schema";

type HttpMethod = 'get' | 'put' | 'post' | 'delete' | 'options' | 'trace' | 'patch' | 'head'
type AllowedUrl = URL | string

export enum Type {
  Core = 'api',
  AssetManagement = 'asset-management.services',
  TariffManagement = 'tariff-management.services'
}

export function applyPagingToUrl(url: URL, page?: number, pageSize?: number) {
  if (page !== undefined && page > 1) {
    url.searchParams.set('page', page.toString())
  }

  if (pageSize !== undefined && pageSize > 1) {
    url.searchParams.set('limit', pageSize.toString())
  }
}

export function applyFilteringToUrl(url: URL, filter?: string, key: string = 'filter') {
  if (filter !== undefined && filter.trim().length > 0) {
    url.searchParams.set(key, filter)
  }
}

export function applySortingToUrl(url: URL, sort: SortedField[] = [], key: string = 'sort') {
  if (sort.length > 0) {
    sort.forEach(
      x => url.searchParams.append(key, x.direction === 'asc' ? x.field : `-${x.field}`)
    )
  }
}

export async function sendPaginationRequest<TSchema extends z.Schema>(url: URL, schema: TSchema) {
  const { data, meta } = await sendGetRequest(url)
  assertSchema(data, z.array(schema))
  assertSchema(meta, z.object({ page: z.number(), pageSize: z.number(), totalRecords: z.number() }))

  return { records: data, paging: meta }
}

export function buildApiUrl(type: Type, path: string = '/') {
  return new URL(
    path,
    {
      [Type.Core]: import.meta.env.VITE_API_BASE_URL,
      [Type.AssetManagement]: import.meta.env.VITE_ASSET_MANAGEMENT_API_URL,
      [Type.TariffManagement]: import.meta.env.VITE_TARIFF_MANAGEMENT_API_URL
    }[type]
  )
}

export function sendGetRequest<TData, TMeta = unknown>(url: URL, init?: RequestInit) {
  return sendRequest<TData, TMeta>({
    method: 'get',
    url,
    init
  })
}

export function sendPatchRequest<TData, TBody = unknown, TMeta = unknown>(url: URL, body?: TBody, init?: RequestInit) {
  return sendRequest<TData, TMeta>({
    method: 'patch',
    url,
    init: {
      ...(init || {}),
      body: JSON.stringify(body)
    }
  })
}

export function sendPostRequest<TData, TBody = unknown, TMeta = unknown>(url: URL, body?: TBody, init?: RequestInit) {
  return sendRequest<TData, TMeta>({
    method: 'post',
    url,
    init: {
      ...(init || {}),
      body: JSON.stringify(body)
    }
  })
}

export function sendPutRequest<TData, TBody = unknown, TMeta = unknown>(url: URL, body?: TBody, init?: RequestInit) {
  return sendRequest<TData, TMeta>({
    method: 'put',
    url,
    init: {
      ...(init || {}),
      body: JSON.stringify(body)
    }
  })
}

export function sendDeleteRequest<TData, TMeta = unknown>(url: URL, init?: RequestInit) {
  return sendRequest<TData, TMeta>({
    method: 'delete',
    url,
    init
  })
}

export default function makeApiClient(type: Type) {
  const baseUrl = {
    [Type.Core]: import.meta.env.VITE_API_BASE_URL,
    [Type.AssetManagement]: import.meta.env.VITE_ASSET_MANAGEMENT_API_URL,
    [Type.TariffManagement]: import.meta.env.VITE_TARIFF_MANAGEMENT_API_URL
  }[type]

  if (baseUrl === undefined) {
    throw new Error(`unsupported api type [${type}] provided`)
  }

  return {
    sendGetRequest<TData, TMeta = unknown>(url: AllowedUrl, init?: RequestInit) {
      return sendRequest<TData, TMeta>({
        method: 'get',
        url: new URL(url, baseUrl),
        init
      })
    },

    sendPatchRequest<TData, TBody = unknown, TMeta = unknown>(url: AllowedUrl, body?: TBody, init?: RequestInit) {
      return sendRequest<TData, TMeta>({
        method: 'patch',
        url: new URL(url, baseUrl),
        init: {
          ...(init || {}),
          body: JSON.stringify(body)
        }
      })
    },

    sendPostRequest<TData, TBody = unknown, TMeta = unknown>(url: AllowedUrl, body?: TBody, init?: RequestInit) {
      return sendRequest<TData, TMeta>({
        method: 'post',
        url: new URL(url, baseUrl),
        init: {
          ...(init || {}),
          body: JSON.stringify(body)
        }
      })
    },

    sendPutRequest<TData, TBody = unknown, TMeta = unknown>(url: AllowedUrl, body?: TBody, init?: RequestInit) {
      return sendRequest<TData, TMeta>({
        method: 'put',
        url: new URL(url, baseUrl),
        init: {
          ...(init || {}),
          body: JSON.stringify(body)
        }
      })
    },

    sendDeleteRequest<TData, TMeta = unknown>(url: AllowedUrl, init?: RequestInit) {
      return sendRequest<TData, TMeta>({
        method: 'delete',
        url: new URL(url, baseUrl),
        init
      })
    },
  }
}


export class ErrorResponse extends Error {
  readonly response: Response
  readonly errors
  readonly body: unknown

  constructor(
    response: Response,
    message?: string,
    errors: { title: string, pointer?: string, detail?: string }[] = [],
    body?: unknown
  ) {
    super(message || `Received bad status code (expected 200-299, got ${response.status})`)
    this.response = response
    this.errors   = errors
    this.body     = body
  }
}

async function sendRequest<TData, TMeta>({
  method,
  url,
  init
}: {
  method: HttpMethod
  url: URL
  init?: RequestInit
}): Promise<{ data: TData; meta: TMeta | undefined; response: Response } | { data: undefined; meta: undefined; response: Response }> {
  init             = init || {}
  init.credentials = 'include'
  init.method      = method.toUpperCase()
  init.headers     = new Headers(init.headers)

  init.headers.set('accept', 'application/json')
  if (!init.headers.has('content-type')) {
    init.headers.set('content-type', 'application/json')
  }

  const response = await fetch(url.toString(), init)

  if (response.ok) {
    if (!(response.headers.get('content-type') || '').startsWith('application/json')) {
      return { data: undefined, meta: undefined, response }
    }

    const json = await response.json()

    if (isObject(json) && 'data' in json)  return { data: json.data,  meta: json.meta,  response }
    if (isObject(json) && 'Data' in json)  return { data: json.Data,  meta: json.Meta,  response }
    if (isObject(json) && 'items' in json) return { data: json.items, meta: json.meta,  response }

    return { data: json, meta: undefined, response }
  }

  // ── Failed response ────────────────────────────────────────────────────────
  let body:   unknown                    = undefined
  let errors: { title: string }[]        = []

  try {
    const json = await response.json()
    body = json

    if (isObject(json) && 'errors' in json && Array.isArray(json.errors)) {
      errors = json.errors
    }
  } catch {
    // non-JSON error body — ignore
  }

  throw new ErrorResponse(response, undefined, errors, body)
}
